import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, CheckCircle, Warning, QrCode, ArrowsClockwise } from '@phosphor-icons/react';

const networks = [
  { id: 'eth', name: 'Ethereum (ERC-20)', token: 'USDT/USDC' },
  { id: 'bsc', name: 'BNB Chain (BEP-20)', token: 'USDT/USDC' },
  { id: 'polygon', name: 'Polygon', token: 'USDT/USDC' },
  { id: 'arbitrum', name: 'Arbitrum', token: 'USDT/USDC' },
  { id: 'tron', name: 'Tron (TRC-20)', token: 'USDT' },
];

const PaymentModal = ({ isOpen, onClose, plan }) => {
  const [selectedNetwork, setSelectedNetwork] = React.useState(networks[0]);
  const [copied, setCopied] = React.useState(false);
  const [timeLeft, setTimeLeft] = React.useState(900);

  const walletAddress = selectedNetwork.id === 'tron' 
    ? 'TJktFCYojHUDyvGaHzc1XCxXhdXbrjFHkQ'
    : '0x34B838B03bc963676a5B4A568271f43CF5e1dE39';

  React.useEffect(() => {
    if (!isOpen) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => { if (prev <= 1) { clearInterval(timer); return 0; } return prev - 1; });
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen]);

  React.useEffect(() => { if (isOpen) setTimeLeft(900); }, [isOpen]);

  const formatTime = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
  const handleCopy = () => { navigator.clipboard.writeText(walletAddress); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  if (!plan) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md" onClick={onClose} data-testid="payment-modal-overlay">
          <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[var(--bg-surface-1)] rounded-2xl border border-[var(--color-primary)]/20 shadow-[0_0_60px_rgba(0,229,255,0.15)] overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="payment-modal">
            <div className="flex items-center justify-between p-6 border-b border-[var(--border-default)] bg-gradient-to-r from-[var(--color-primary)]/10 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[var(--color-primary)]/20 flex items-center justify-center"><QrCode size={24} className="text-[var(--color-primary)]" weight="bold" /></div>
                <div><h3 className="text-lg font-semibold text-[var(--text-primary)]">Payment Details</h3><p className="text-sm text-[var(--text-secondary)]">{plan.name} Plan</p></div>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-[var(--bg-surface-2)] text-[var(--text-secondary)]" data-testid="payment-modal-close"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="text-sm text-[var(--text-secondary)] mb-2 block">Select Network</label>
                <div className="grid grid-cols-2 gap-2">
                  {networks.map((n) => (
                    <button key={n.id} onClick={() => setSelectedNetwork(n)} className={`p-3 rounded-lg border text-left transition-all ${selectedNetwork.id === n.id ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10' : 'border-[var(--border-default)] hover:border-[var(--color-primary)]/30'}`} data-testid={`network-${n.id}`}>
                      <p className="text-sm font-medium text-[var(--text-primary)]">{n.name}</p><p className="text-xs text-[var(--text-secondary)]">{n.token}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm text-[var(--text-secondary)] mb-2 block">Payment Address</label>
                <div className="flex items-center gap-2 p-4 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border-default)]">
                  <code className="flex-1 text-sm text-[var(--text-primary)] font-mono truncate">{walletAddress}</code>
                  <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={handleCopy} className="p-2 rounded-lg bg-[var(--color-primary)] text-[var(--bg-primary)]" data-testid="copy-address-btn">
                    {copied ? <CheckCircle size={20} weight="bold" /> : <Copy size={20} weight="bold" />}
                  </motion.button>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border-default)] text-center">
                <p className="text-sm text-[var(--text-secondary)] mb-2">Payment Amount</p>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <span className="text-3xl font-bold text-[var(--color-primary)]">{plan.price}</span>
                  <span className="text-lg text-[var(--text-primary)]">USDT</span>
                  <span className="text-[var(--text-secondary)]">or</span>
                  <span className="text-3xl font-bold text-[var(--accent-success)]">{plan.price}</span>
                  <span className="text-lg text-[var(--text-primary)]">USDC</span>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-[var(--accent-danger)]/10 border border-[var(--accent-danger)]/20">
                <ArrowsClockwise size={18} className="text-[var(--accent-danger)]" /><span className="text-sm text-[var(--text-secondary)]">Time remaining: <span className="font-mono text-[var(--accent-danger)]">{formatTime(timeLeft)}</span></span>
              </div>
              <div className="space-y-3">
                <p className="text-sm font-medium text-[var(--text-primary)]">Before you pay:</p>
                {['After payment, your account upgrades automatically.', 'Use the wallet you will pay from.', 'Send the EXACT amount listed.'].map((t, i) => (
                  <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-[var(--accent-success)]/10 border-l-2 border-[var(--accent-success)]"><CheckCircle size={18} className="text-[var(--accent-success)] mt-0.5" weight="fill" /><p className="text-sm text-[var(--text-secondary)]">{t}</p></div>
                ))}
                <div className="flex items-start gap-2 p-3 rounded-lg bg-[var(--accent-danger)]/10 border-l-2 border-[var(--accent-danger)]"><X size={18} className="text-[var(--accent-danger)] mt-0.5" weight="bold" /><p className="text-sm text-[var(--text-secondary)]">Do not pay from someone else's account.</p></div>
              </div>
              <div className="p-4 rounded-xl bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/30">
                <div className="flex items-start gap-2"><Warning size={20} className="text-[var(--color-primary)] mt-0.5" weight="fill" /><div><p className="text-sm font-medium text-[var(--color-primary)]">IMPORTANT</p><p className="text-sm text-[var(--text-secondary)]">The paying wallet address gets access to the bot. If you send from another account, that account will have access instead.</p></div></div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PaymentModal;
